import styled from '@emotion/styled';
import {
  duration,
  easing,
  layout,
  palette,
  radius,
  shadow,
  spacing,
  transition,
  typography,
  zIndex,
  status,
} from '../../style-utils/theme';
import { down, touch } from '../../style-utils/media-queries';
import { m } from '../../motion';

export const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: ${spacing.md};
`;

export const SectionHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${spacing.sm};
  flex-wrap: wrap;
`;

export const SectionTitle = styled.h3`
  margin: 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.md}px;
  font-weight: ${typography.weight.semibold};
  letter-spacing: ${typography.tracking.snug};
  color: ${palette.ink};
`;

export const Count = styled.span`
  margin-left: ${spacing.xs};
  font-size: ${typography.size.sm}px;
  font-weight: ${typography.weight.regular};
  color: ${palette.slateLight};
`;

/* auto-fill, not auto-fit: with auto-fit a gallery holding one photo stretches
 * that tile across the whole row, and a 1:1 tile stretched to 900px is a wall
 * of image where a thumbnail was meant to be. */
export const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  gap: ${spacing.sm};

  ${down('sm')} {
    grid-template-columns: repeat(auto-fill, minmax(132px, 1fr));
  }
`;

/**
 * The class the action overlay carries, so the TILE can reveal it on hover.
 *
 * WHY A STRING AND NOT `${TileActions}`. Emotion's component selector —
 * interpolating a styled component into another one's CSS — is compiled away by
 * `@emotion/babel-plugin`, which stamps each component with a stable target
 * class. CRA runs `babel-preset-react-app` and does NOT include that plugin, and
 * there is no craco/rewired config here to add it, so at runtime the component
 * has no selector to give and emotion throws:
 *
 *   "Component selectors can only be used in conjunction with
 *    @emotion/babel-plugin…"
 *
 * It threw during render, so it took the whole screen down with the CRA overlay
 * — and only once a tile existed to draw actions over, which is why it looked
 * like an upload bug rather than a styling one.
 *
 * dashboard-home.styled.tsx made this call the same way, until the dashboard
 * consolidation deleted that screen. dashboard-screen/charts/chart.styled.tsx
 * carries the rule now.
 */
export const TILE_ACTIONS_CLASS = 'tile-actions';

export const Tile = styled.div<{ $dragging?: boolean; $over?: boolean }>`
  position: relative;
  aspect-ratio: 1 / 1;
  border-radius: ${radius.md};
  overflow: hidden;
  background: ${palette.surfaceMuted};
  box-shadow: ${shadow.xs};
  cursor: grab;
  opacity: ${({ $dragging }) => ($dragging ? 0.4 : 1)};
  outline: ${({ $over }) =>
    $over ? `2px solid ${palette.teal}` : '2px solid transparent'};
  outline-offset: 2px;
  transition: ${transition.colors}, opacity ${duration.fast} ${easing.standard};

  &:active {
    cursor: grabbing;
  }

  &:hover {
    box-shadow: ${shadow.md};
  }

  /* THE REVEAL LIVES ON THE PARENT. It used to sit in TileActions as
     "${'$'}{Tile}:hover &" — see TILE_ACTIONS_CLASS above for why that could not
     work under CRA. Written from here it needs no component selector at all:
     the tile already has "&". */
  &:hover .${TILE_ACTIONS_CLASS},
  &:focus-within .${TILE_ACTIONS_CLASS} {
    opacity: 1;
  }
`;

/* A motion image: it is drawn transparent and fades in on its own load event
   (see the component) - the tile's tinted ground shows until the bytes are
   there, instead of the whole tile fading in before the image exists. */
export const TileImage = m.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  /* The image is already square, so this never crops — it only guards against a
   * legacy row whose blob was stored before the 1:1 rule existed. */
`;

/* Actions sit on a scrim that fades in on hover, and are ALWAYS visible on
 * touch, where there is no hover to reveal them. */
export const TileActions = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: ${spacing.half};
  padding: ${spacing.xs};
  background: linear-gradient(
    to top,
    rgba(18, 41, 44, 0.72) 0%,
    rgba(18, 41, 44, 0) 55%
  );
  opacity: 0;
  transition: opacity ${duration.fast} ${easing.standard};

  /* THE SCRIM IS NOT A TARGET. It covers the whole tile, so while it took the
     pointer a click on the photo landed here and never reached the image's
     onClick — anyone who could edit a gallery could not open a photo in the
     viewer (production journey, 2026-10-14). Only the buttons take clicks. */
  pointer-events: none;

  & > * {
    pointer-events: auto;
  }

  /* The hover reveal is on Tile, keyed on TILE_ACTIONS_CLASS — which the
     consumer must put on this element (entity-gallery.tsx). */

  @media (hover: none) {
    opacity: 1;
  }
`;

export const TileButton = styled.button<{ $danger?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 32px;
  min-height: 32px;
  /* 32px is right under a mouse — these sit on a hover overlay over the tile
     and a bigger box would cover the photo they act on. Under a thumb 32px is
     below the 44px minimum (design_system rule 5), and the overlay is
     permanently visible there anyway (the hover: none rule further up), so
     grow it only for touch. */
  ${touch} {
    min-width: ${layout.touchTarget};
    min-height: ${layout.touchTarget};
  }
  padding: 0 ${spacing.xs};
  border: none;
  border-radius: ${radius.xs};
  background: ${palette.surface};
  color: ${({ $danger }) => ($danger ? status.alert.fg : palette.inkSoft)};
  font-family: ${typography.ui};
  font-size: ${typography.size.xxs}px;
  font-weight: ${typography.weight.semibold};
  cursor: pointer;
  transition: ${transition.colors};

  &:hover:not(:disabled) {
    background: ${({ $danger }) =>
      $danger ? palette.alert : palette.tealTint};
    color: ${({ $danger }) => ($danger ? palette.surface : palette.lagoon)};
  }

  &:focus-visible {
    outline: none;
    box-shadow: ${shadow.focus};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

/* The cover marker. A badge, not a star icon: "Cover" is unambiguous and needs
 * no legend, and the grid is small enough that an icon would be guessed at. */
export const CoverBadge = styled.span`
  position: absolute;
  top: ${spacing.xs};
  left: ${spacing.xs};
  padding: ${spacing.half} ${spacing.xs};
  border-radius: ${radius.pill};
  background: ${palette.teal};
  color: ${palette.surface};
  font-family: ${typography.ui};
  font-size: ${typography.size.xxs}px;
  font-weight: ${typography.weight.semibold};
  letter-spacing: ${typography.tracking.caps};
  text-transform: uppercase;
  box-shadow: ${shadow.sm};
`;

/* The add tile lives in the grid rather than beside it, so "add" is always at
 * the end of the photos instead of jumping to a toolbar the eye has left. */
export const AddTile = styled.button`
  aspect-ratio: 1 / 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${spacing.xs};
  border: 1px dashed ${palette.lineStrong};
  border-radius: ${radius.md};
  background: ${palette.surfaceMuted};
  color: ${palette.slate};
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  font-weight: ${typography.weight.medium};
  cursor: pointer;
  transition: ${transition.colors};

  &:hover:not(:disabled) {
    border-color: ${palette.lagoon};
    border-style: solid;
    background: ${palette.tealTintSoft};
    color: ${palette.lagoon};
  }

  &:focus-visible {
    outline: none;
    box-shadow: ${shadow.focus};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

export const AddGlyph = styled.span`
  font-size: ${typography.size.xxl}px;
  font-weight: ${typography.weight.light};
  line-height: 1;
`;

export const Empty = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${spacing.xs};
  padding: ${spacing.xl} ${spacing.md};
  border: 1px dashed ${palette.line};
  border-radius: ${radius.md};
  background: ${palette.surfaceMuted};
  text-align: center;
`;

export const EmptyTitle = styled.p`
  margin: 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.base}px;
  font-weight: ${typography.weight.semibold};
  color: ${palette.inkSoft};
`;

export const EmptyText = styled.p`
  margin: 0;
  max-width: 44ch;
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  line-height: ${typography.leading.normal};
  color: ${palette.slate};
`;

export const Notice = styled.p<{ $tone?: 'error' | 'muted' }>`
  margin: 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  color: ${({ $tone }) =>
    $tone === 'error' ? palette.alert : palette.slate};
`;

/* Upload progress. A determinate bar, because the upload is a known size and a
 * spinner would say "something is happening" when the honest answer is "62%". */
export const ProgressTrack = styled.div`
  height: 4px;
  border-radius: ${radius.pill};
  background: ${palette.line};
  overflow: hidden;
`;

export const ProgressFill = styled.div<{ $percent: number }>`
  height: 100%;
  width: ${({ $percent }) => $percent}%;
  border-radius: ${radius.pill};
  background: ${palette.teal};
  transition: width ${duration.base} ${easing.standard};
`;

/* ── Lightbox ───────────────────────────────────────────────────────── */

export const ViewerScrim = m.div`
  position: fixed;
  inset: 0;
  z-index: ${zIndex.modal};
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(18, 41, 44, 0.88);
`;

export const ViewerStage = m.div`
  position: relative;
  width: min(92vw, 92vh);
  width: min(92dvw, 92dvh);
  height: min(92vw, 92vh);
  height: min(92dvw, 92dvh);
  overflow: hidden;
  border-radius: ${radius.md};
  touch-action: none;
`;

export const ViewerImage = styled.img<{ $scale: number; $x: number; $y: number }>`
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
  transform: translate(${({ $x }) => $x}px, ${({ $y }) => $y}px)
    scale(${({ $scale }) => $scale});
  transform-origin: center;
  transition: transform ${duration.fast} ${easing.standard};
  cursor: ${({ $scale }) => ($scale > 1 ? 'grab' : 'zoom-in')};
  user-select: none;

  &:active {
    cursor: ${({ $scale }) => ($scale > 1 ? 'grabbing' : 'zoom-in')};
  }
`;

export const ViewerBar = m.div`
  position: fixed;
  left: 50%;
  bottom: ${spacing.lg};
  transform: translateX(-50%);
  z-index: ${zIndex.modal};
  display: flex;
  align-items: center;
  gap: ${spacing.xs};
  padding: ${spacing.xs} ${spacing.sm};
  border-radius: ${radius.pill};
  background: ${palette.surface};
  box-shadow: ${shadow.lg};
`;

export const ViewerCount = styled.span`
  min-width: 56px;
  text-align: center;
  font-family: ${typography.ui};
  font-size: ${typography.size.xs}px;
  font-weight: ${typography.weight.medium};
  color: ${palette.slate};
`;

export const ViewerClose = m.button`
  position: fixed;
  top: ${spacing.md};
  right: ${spacing.md};
  z-index: ${zIndex.modal};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border: none;
  border-radius: ${radius.circle};
  background: ${palette.surface};
  color: ${palette.ink};
  font-size: ${typography.size.lg}px;
  line-height: 1;
  cursor: pointer;
  box-shadow: ${shadow.md};
  transition: ${transition.colors};

  &:hover {
    background: ${palette.tealTint};
    color: ${palette.lagoon};
  }

  &:focus-visible {
    outline: none;
    box-shadow: ${shadow.focus};
  }
`;
